import {
  clockTime,
  startOfDay,
} from 'src/components/Chat/components/MessageContainer/components/EventBubble/components/FollowUpBubble/FollowUpBubble.functions';
import type {
  tMarketingEligibility,
  tSchedulingContent,
  tSchedulingRisk,
  tWhen,
} from './SchedulingRiskWarning.types';

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;

/** Meta's customer service window: 24 h after the lead's last message. */
export const CONVERSATION_WINDOW_MS = DAY_MS;

/** How far back a 131049 still predicts the next marketing send fails (D6). */
export const RECENT_ENGAGEMENT_CAP_MS = 30 * DAY_MS;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readTime(value: unknown): Date | null {
  if (typeof value !== 'string' || !value) return null;
  const time = new Date(value);
  return Number.isNaN(time.getTime()) ? null : time;
}

/**
 * INT-1500 U13 (contract C8, extended by review-driven change #9): reads the
 * body bff-web relays from whatsapp-business-service's marketing-eligibility
 * route, `{ handle, marketingBackoffUntil, marketingOptedOutAt,
 * lastEngagementCapAt, lastExperimentAt, conversationWindowExpiresAt,
 * lastInboundAt }` (RFC 3339 or null). A body that is not an object reads as
 * null, so the modal warns about nothing. A field that is not a readable time
 * reads as null (nothing known). `lastInboundAt` is additive: when the key is
 * absent from the body (an older whatsapp-business-service that predates it),
 * the parsed result omits it too, rather than defaulting it to null, so
 * `evaluateSchedulingRisk` can tell "not known" from "not told" and fall back
 * to deriving it from `conversationWindowExpiresAt` only in the latter case.
 */
export function parseMarketingEligibility(
  raw: unknown,
): tMarketingEligibility | null {
  if (!isRecord(raw)) return null;

  return {
    marketingBackoffUntil: readTime(raw.marketingBackoffUntil),
    marketingOptedOutAt: readTime(raw.marketingOptedOutAt),
    lastEngagementCapAt: readTime(raw.lastEngagementCapAt),
    lastExperimentAt: readTime(raw.lastExperimentAt),
    conversationWindowExpiresAt: readTime(raw.conversationWindowExpiresAt),
    ...(raw.lastInboundAt !== undefined
      ? { lastInboundAt: readTime(raw.lastInboundAt) }
      : {}),
  };
}

/**
 * Whether the route can be asked about this handle at all: bff-web and
 * whatsapp-business-service accept 10 to 15 digits with an optional leading
 * '+', and answer anything else with a 400. Anything else (a username-only
 * contact, say) is not asked, and the modal stays as today.
 */
export function isEligibilityHandle(handle: string | null | undefined): boolean {
  return Boolean(handle && /^\+?\d{10,15}$/.test(handle));
}

/** The modal's date and time fields as one local instant, or null while either is empty. */
export function buildTriggerAt(
  triggerDate: string | undefined,
  triggerTime: string | undefined,
): Date | null {
  if (!triggerDate || !triggerTime) return null;
  const triggerAt = new Date(`${triggerDate}T${triggerTime}:00`);
  return Number.isNaN(triggerAt.getTime()) ? null : triggerAt;
}

/**
 * The first minute the modal can offer that is strictly after the hold: the
 * back-off refuses while the hold is in the future, and the fields hold whole
 * minutes.
 */
export function firstSlotAfter(holdUntil: Date): Date {
  const slot = new Date(holdUntil.getTime());
  slot.setSeconds(0, 0);
  return new Date(slot.getTime() + MINUTE_MS);
}

type tEvaluateSchedulingRiskInput = {
  eligibility: tMarketingEligibility | null;
  triggerAt: Date | null;
  content: tSchedulingContent;
  now: Date;
};

/**
 * INT-1500 U13 (D6, D16): the warning, if any, for sending this content to
 * this lead at this time. Pure; warns, never blocks.
 *
 * Marketing templates only (UTILITY, session replies and authentication
 * templates are not affected by any of these), in this order:
 * 1. Opted out (131050): Leadsales will not send marketing to this lead.
 * 2. The send falls inside the hold a 131049 armed: Leadsales will not send it.
 * 3. 130472: the lead is in Meta's experiment and only receives marketing
 *    inside a window the lead opens.
 * 4. A 131049 in the last 30 days with no message from the lead since.
 * Free text: the send falls after the 24 h window closes, or no window is open.
 *
 * "No message since" reads `eligibility.lastInboundAt` when
 * whatsapp-business-service sent it (review-driven contract addition #9): the
 * contact's last inbound time regardless of whether the window it opened is
 * still current, so a lead who replied after the 131049 but whose window has
 * since re-closed is still known to have written. An older
 * whatsapp-business-service that omits the key falls back to the window: the
 * last message from the lead is the window's close minus 24 h, or not known
 * to have written when no window is on record.
 */
export function evaluateSchedulingRisk(
  input: tEvaluateSchedulingRiskInput,
): tSchedulingRisk | null {
  const { eligibility, triggerAt, content, now } = input;
  // A time already past is the modal's own error ("ya pasaron"), not ours.
  if (!eligibility || !triggerAt || triggerAt.getTime() <= now.getTime()) {
    return null;
  }

  const windowExpiresAt = eligibility.conversationWindowExpiresAt;

  if (content.kind === 'text') {
    const windowOpenAtTrigger =
      windowExpiresAt !== null &&
      windowExpiresAt.getTime() > now.getTime() &&
      triggerAt.getTime() <= windowExpiresAt.getTime();
    return windowOpenAtTrigger ? null : { rule: 'windowClosed', windowExpiresAt };
  }

  if (content.category !== 'MARKETING') return null;

  if (eligibility.marketingOptedOutAt) {
    return { rule: 'optedOut', optedOutAt: eligibility.marketingOptedOutAt };
  }

  const derivedLastInboundAt = windowExpiresAt
    ? new Date(windowExpiresAt.getTime() - CONVERSATION_WINDOW_MS)
    : null;
  const lastInboundAt =
    eligibility.lastInboundAt !== undefined
      ? eligibility.lastInboundAt
      : derivedLastInboundAt;
  const capAt = eligibility.lastEngagementCapAt;
  const repliedSinceCap =
    lastInboundAt !== null &&
    capAt !== null &&
    lastInboundAt.getTime() > capAt.getTime();

  const holdUntil = eligibility.marketingBackoffUntil;
  if (holdUntil && triggerAt.getTime() < holdUntil.getTime()) {
    return { rule: 'marketingHold', holdUntil, capAt, repliedSinceCap };
  }

  if (eligibility.lastExperimentAt) {
    return { rule: 'experiment', experimentAt: eligibility.lastExperimentAt };
  }

  if (
    capAt &&
    now.getTime() - capAt.getTime() <= RECENT_ENGAGEMENT_CAP_MS &&
    !repliedSinceCap
  ) {
    return { rule: 'recentCap', capAt };
  }

  return null;
}

/**
 * Where a date sits next to "now", in the reader's local time: today,
 * tomorrow, yesterday, or a date ("15 sep", with the year only when it is not
 * this year). The copy turns it into "hoy 18:40", "mañana 18:40", "ayer 10:02"
 * or "el 15 sep 14:30" (voice-and-tone.md, dates).
 */
export function describeWhen(date: Date, now: Date): tWhen {
  const time = clockTime(date);
  const dayDiff = Math.round((startOfDay(date) - startOfDay(now)) / DAY_MS);
  let kind: tWhen['kind'] = 'date';
  if (dayDiff === 0) kind = 'today';
  else if (dayDiff === 1) kind = 'tomorrow';
  else if (dayDiff === -1) kind = 'yesterday';

  return {
    kind,
    time,
    day: date.getDate(),
    monthIndex: date.getMonth(),
    year: date.getFullYear() === now.getFullYear() ? null : date.getFullYear(),
  };
}
